/*
 * Copyright (c) 2020. American Express Company
 */

import * as _ from 'lodash';
import { DateTimeFormatter, IsoFields, LocalDate, ChronoUnit, LocalDateTime } from '@js-joda/core';
const { Locale } = require('@js-joda/locale_en-us');

export default class FormatService {
    // create all formatters in advance so we don't spend time creating when formatting
    private static readonly summaryDollarFormatter = Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
        notation: 'compact',
        compactDisplay: 'short'
    } as Intl.NumberFormatOptions);

    private static readonly currencyFormatter = Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    private static readonly currencyRoundedFormatter = Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });

    private static readonly roundedFormatter = Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });

    /**
     * Formats a number into USD currency formatting, including '$'
     * Example: 1234.56 => '$1,234.56'
     * @param {number} [number=0] - The number to be formatted
     * @param {boolean} [options.round] - Optional rounding, rounds up when value >= .5 or down otherwise
     * @param {boolean} [options.sign] - Optional sign, adds a '+' before positive numbers (excluding 0)
     */
    public static formatCurrency(
        number: number = 0,
        { round, sign }: { round?: boolean; sign?: boolean } = {}
    ): string {
        if (isNaN(number)) return '';
        const formatter: Intl.NumberFormat = round ? this.currencyRoundedFormatter : this.currencyFormatter;
        const formattedNumber = formatter.format(number);

        if (sign && number > 0) return `+${formattedNumber}`;
        return formattedNumber;
    }

    // round summaries to 10s of dollars
    public static formatSummary(amount: number): string {
        if (isNaN(amount) || amount === 0) amount = 0;
        return this.summaryDollarFormatter.format(Math.round(amount / 10) * 10);
    }

    // old version did not work on Safari
    public static formatCompactMoney(num: number, digits: number = 1): string {
        if (isNaN(num) || num === 0) num = 0;

        const negative = num < 0;
        num = Math.abs(num);

        const si = [
            { value: 1, symbol: '' },
            { value: 1e3, symbol: 'K' },
            { value: 1e6, symbol: 'M' },
            { value: 1e9, symbol: 'G' },
            { value: 1e12, symbol: 'T' },
            { value: 1e15, symbol: 'P' },
            { value: 1e18, symbol: 'E' }
        ];
        const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
        let i;
        for (i = si.length - 1; i > 0; i--) {
            if (num >= si[i].value) {
                break;
            }
        }
        const retValue = '$' + (num / si[i].value).toFixed(digits).replace(rx, '$1') + si[i].symbol;

        return negative ? '-' + retValue : retValue;
    }

    public static formatString(amount: number): string {
        if (!amount) {
            return this.currencyRoundedFormatter.format(0);
        }
        let samount: string;
        try {
            samount = String(amount);
            samount = samount.replace(',', '');
            samount = samount.replace('$', '');
        } catch (error) {
            return this.currencyRoundedFormatter.format(0);
        }

        return this.currencyRoundedFormatter.format(Number(samount));
    }

    public static format(amount: number, showPlusIfPositive?: boolean): string {
        if (isNaN(amount) || amount === 0) amount = 0;
        return (showPlusIfPositive && amount > 0 ? '+' : '') + this.currencyRoundedFormatter.format(amount);
    }

    public static unformatAmount(amount: string): string {
        amount = amount.replace(/\D+/g, '');
        return amount;
    }

    public static formatPercentage(percentage: number): string {
        return `${_.round(percentage || 0)}%`;
    }

    public static formatToCamelCase(value: string): string {
        return _.camelCase(value);
    }

    /** round to nearest whole number, without currency (dollar) symbol */
    public static formatRounded(amount: number): string {
        if (isNaN(amount) || amount === 0) amount = 0;
        return this.roundedFormatter.format(amount);
    }

    /** date format for SQL 2020-08-01 */
    static dateYearMonthDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('yyyy-MM-01');

    public static formatYearMonthDate(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateSqlDisplayFormatter);
    }

    /** date format for SQL 2020-08-17 */
    static dateSqlDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('yyyy-MM-dd');

    // TODO: this is the same as the formatYearMonthDate function, so possibly remove?
    public static formatSqlDate(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateSqlDisplayFormatter);
    }

    /** date format for display */
    static dateDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MM/dd/yyyy');

    public static formatDate(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateDisplayFormatter);
    }

    static dateFullFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MMMM d, yyyy').withLocale(Locale.US);

    public static formatDateFull(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateFullFormatter);
    }

    /** date format for display */
    static dateShortWithYearDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('M/dd/yy');
    static dateShortWithYearLeadingZeroDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MM/dd/yy');

    public static formatShortDateWithYear(date: LocalDate, leadingZero: boolean = false): string {
        if (leadingZero) {
            return date?.format(this.dateShortWithYearLeadingZeroDisplayFormatter);
        }
        return date?.format(this.dateShortWithYearDisplayFormatter);
    }

    /** date format for display */
    static dateShortDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MM/dd');

    public static formatShortDate(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateShortDisplayFormatter);
    }

    public static formatMonthStart(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.monthDisplayFormatter)[0];
    }

    static dateMonthDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MMM yyyy').withLocale(Locale.US);

    public static formatDateMonth(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateMonthDisplayFormatter);
    }

    static monthDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MMM').withLocale(Locale.US);

    public static formatMonth(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.monthDisplayFormatter);
    }

    static shortMonthDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MMMMM').withLocale(Locale.US);

    public static formatShortMonth(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.shortMonthDisplayFormatter);
    }

    static dateLongMonthDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MMMM, yyyy').withLocale(
        Locale.US
    );

    public static formatDateLongMonth(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateLongMonthDisplayFormatter);
    }

    static dateAltLongMonthDisplayFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MMMM yyyy').withLocale(
        Locale.US
    );

    public static formatAltDateLongMonth(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateAltLongMonthDisplayFormatter);
    }

    static dateMDYFormatterAbbreviated: DateTimeFormatter = DateTimeFormatter.ofPattern('MMM. d, yyyy').withLocale(
        Locale.US
    );

    public static formatMonthDayYearAbbreviated(date: LocalDate): string {
        return date.format(this.dateMDYFormatterAbbreviated);
    }

    static dateMDFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('MMMM dd').withLocale(Locale.US);

    public static formatMonthDay(date: LocalDate): string {
        if (_.isString(date)) {
            return date;
        }
        return date.format(this.dateMDFormatter);
    }

    static quarterFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('Q yyyy').withLocale(Locale.US);
    static yearFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('yyyy').withLocale(Locale.US);

    public static formatTime(localDateTime: LocalDateTime, withAmPm?: boolean): string {
        const dateTimeFormatter = DateTimeFormatter.ofPattern(`h:mm${withAmPm ? ' a' : ''}`).withLocale(Locale.US);
        return localDateTime.format(dateTimeFormatter);
    }

    public static formatPeriod(zoom: string, date: LocalDate): string {
        switch (zoom) {
            case 'months':
                return date.format(this.dateMonthDisplayFormatter);

            case 'quarters':
                return 'Q' + date.format(this.quarterFormatter);

            case 'years':
            case 'overall':
                return date.format(this.yearFormatter);
        }
    }

    static quarterShortFormatter: DateTimeFormatter = DateTimeFormatter.ofPattern('Q').withLocale(Locale.US);

    public static formatPeriodShort(zoom: string, date: LocalDate): string {
        if (!date || !zoom) return;

        switch (zoom) {
            case 'months':
                if (date.monthValue() === 1) {
                    return date.format(this.yearFormatter);
                } else {
                    return date.format(this.monthDisplayFormatter);
                }

            case 'quarters':
                if (date.get(IsoFields.QUARTER_OF_YEAR) === 1) {
                    return date.format(this.yearFormatter);
                } else {
                    return 'Q' + date.format(this.quarterShortFormatter);
                }

            case 'overall':
            case 'years':
                return date.format(this.yearFormatter);
        }
    }

    public static formatRange(zoom: string, startDate: LocalDate, endDate: LocalDate): string {
        const days = startDate.until(endDate, ChronoUnit.DAYS);

        switch (zoom) {
            case 'months':
                if (days < 32) {
                    return FormatService.formatPeriodShort(zoom, startDate);
                }
                break;

            case 'quarters':
                if (days < 93) {
                    return FormatService.formatPeriodShort(zoom, startDate);
                }
                break;

            case 'years':
                if (startDate.monthValue() === 1) {
                    return FormatService.formatPeriodShort(zoom, startDate);
                }
                break;
        }

        return `${startDate.format(this.dateMonthDisplayFormatter)} - ${endDate.format(
            this.dateMonthDisplayFormatter
        )}`;
    }

    /**
     * Return the singular or plural of a given string depending on the given amount
     * eg: pluralize(1, ["thing", "things"]) -> "thing"
     * eg: pluralize(5, ["thing", "things"]) -> "things"
     * @param amount
     * @param polyText an array of strings, first is singular, second is for two, last is for any multiple
     * For english, we generally only have singluar and plural, but there could be special cases for languages
     * that special versions for two or others.
     */
    public static pluralize(amount: number, polyText: string[]): string {
        return polyText[Math.min(polyText.length - 1, amount - 1)];
    }

    public static formatShowWorkRange(zoom: string, projected: boolean, age: number, startDate: LocalDate): string {
        if (projected) {
            return zoom === 'month'
                ? `${FormatService.formatDateMonth(startDate)}, Age ${age}`
                : `${startDate.year()}, Age ${age}`;
        } else {
            return zoom === 'month'
                ? `${FormatService.formatDateMonth(startDate)}, Age ${age}`
                : `${startDate.year()}, Age ${age}`;
        }
    }

    public static formatPercent(value: number, decimalPlaces: number = 1): string {
        return value?.toFixed(decimalPlaces) + '%';
    }

    public static formatInterest(value: number, decimalPlaces: number = 2): string {
        return (value * 100).toFixed(decimalPlaces) + '%';
    }

    public static abbreviate(value: string, length: number): string {
        if (value.length > length) {
            return value.slice(0, length) + '…';
        } else {
            return value;
        }
    }

    public static formatStringToNumber(value: string): number {
        return Number(value.replace(/[^\d.-]/g, ''));
    }

    public static formatTag(value: string): string {
        return value?.toLowerCase().replace(/\s/g, '-') || '';
    }

    public static replaceDigits(value: string, replacement: string = '#'): string {
        return value?.replace(/\d/g, replacement) || '';
    }
}
